import { API_BASE } from '../../config/api';

const STEPS = [
  {
    title: 'Structure',
    description:
      'Organize your expertise, achievements, and goals as structured data with fields, relationships, and context.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
        {/* Database icon */}
        <ellipse cx="16" cy="8" rx="10" ry="4" />
        <path d="M6 8v8c0 2.21 4.477 4 10 4s10-1.79 10-4V8" />
        <path d="M6 16v8c0 2.21 4.477 4 10 4s10-1.79 10-4v-8" />
      </svg>
    ),
  },
  {
    title: 'Expose',
    description:
      'Publish your professional identity as a queryable API endpoint. Make yourself part of the searchable, structured web.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
        {/* Globe / API icon */}
        <circle cx="16" cy="16" r="12" />
        <ellipse cx="16" cy="16" rx="5" ry="12" />
        <path d="M4 16h24" />
        <path d="M6 10h20" />
        <path d="M6 22h20" />
      </svg>
    ),
  },
  {
    title: 'Get Found',
    description:
      'Let AI agents and intelligent recruiting engines discover you through your work, not through keyword-matched PDFs.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
        {/* Radar icon */}
        <circle cx="16" cy="16" r="12" />
        <circle cx="16" cy="16" r="8" strokeDasharray="3 3" />
        <circle cx="16" cy="16" r="4" strokeDasharray="2 2" />
        <circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none" />
        <line x1="16" y1="16" x2="23" y2="9" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function DiscoverableCTA() {
  return (
    <section
      id="discoverable"
      className="rounded-2xl px-6 py-12 sm:px-10 sm:py-16 space-y-10"
      style={{
        background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.25) 0%, rgba(10, 10, 10, 0.9) 100%)',
      }}
    >
      {/* Section header */}
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="text-3xl font-bold text-ti-text mb-3">
          Make Yourself Discoverable
        </h2>
        <p className="text-ti-text-muted leading-relaxed">
          The hiring process is changing. AI agents will search for talent the
          way search engines crawl the web. Will they find you?
        </p>
      </div>

      {/* Step cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {STEPS.map((step, i) => (
          <div
            key={step.title}
            className="bg-ti-surface/60 border border-ti-border rounded-xl p-6 text-center space-y-4 hover:border-ti-blue/30 transition-colors"
          >
            {/* Step number */}
            <div className="flex items-center justify-center gap-2 text-ti-text-dim text-xs font-mono uppercase tracking-wider">
              <span className="w-5 h-5 rounded-full border border-ti-border flex items-center justify-center text-[10px]">
                {i + 1}
              </span>
            </div>

            {/* Icon */}
            <div className="flex justify-center text-ti-blue">{step.icon}</div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-ti-text">{step.title}</h3>

            {/* Description */}
            <p className="text-sm text-ti-text-muted leading-relaxed">
              {step.description}
            </p>
          </div>
        ))}
      </div>

      {/* Explainer */}
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-sm text-ti-text-dim leading-relaxed">
          This prototype queries Ted Lango's Living Resume API in real-time.
          The data you saw above was not a static mockup -- it was fetched live
          from a Cloudflare Worker serving structured professional data.
        </p>
      </div>

      {/* CTA buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <a
          href="https://www.linkedin.com/pulse/front-door-broken-why-future-recruiting-looks-more-like-ted-lango-znwhe"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-ti-blue text-white font-medium rounded-lg hover:bg-ti-blue/90 transition-colors"
        >
          Read the Full Article
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </a>
        <a
          href="https://tedvlango.com/#api"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 border border-ti-border text-ti-text-muted font-medium rounded-lg hover:border-ti-blue/40 hover:text-ti-text transition-colors"
        >
          Explore the Living Resume API
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </section>
  );
}
